import { SprinklesElement } from "sprinkles-js";

/**
 * DropdownMenuElement - A custom element wrapper for <details>-based dropdown menus.
 *
 * Features:
 * - Closes on click outside
 * - Closes on Escape key (innermost menu closes first for nested menus)
 * - Returns focus to trigger on Escape
 *
 * Required refs:
 * - details: The <details> element
 * - trigger: The <summary> element
 *
 * @example
 * <dropdown-menu>
 *   <details class="dropdown-menu" data-dropdown-menu-ref="details">
 *     <summary class="dropdown-menu__trigger" data-dropdown-menu-ref="trigger">
 *       Menu
 *     </summary>
 *     <nav class="dropdown-menu__content">
 *       <a href="#" class="dropdown-menu__item">Item 1</a>
 *       <a href="#" class="dropdown-menu__item">Item 2</a>
 *     </nav>
 *   </details>
 * </dropdown-menu>
 */
export default class DropdownMenuElement extends SprinklesElement {
  static tagName = "dropdown-menu";
  static refs = ["trigger", "details"];
  static events = {
    pointerup: { method: "handleClickOutside", element: document },
    keydown: { method: "handleKeydown", element: document },
  };

  get isOpen() {
    return this.refs.details?.open;
  }

  close() {
    if (this.refs.details) this.refs.details.open = false;
  }

  handleClickOutside(event) {
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (!this.isOpen || event.key !== "Escape") return;

    // Skip if a nested menu is open (let it close first)
    const childMenus = this.querySelectorAll("dropdown-menu");
    const hasOpenChild = Array.from(childMenus).some(
      (menu) => menu.refs.details?.open,
    );
    if (hasOpenChild) return;

    event.preventDefault();
    this.close();
    this.refs.trigger?.focus();
  }
}
